import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie, X } from "lucide-react";

const CONSENT_KEY = "cookie_consent";
// Values: "accept-all" | "reject-non-essential" | "custom"

function readConsent() {
  try { return JSON.parse(localStorage.getItem(CONSENT_KEY) || "null"); }
  catch { return null; }
}

function writeConsent(value) {
  try {
    localStorage.setItem(CONSENT_KEY, JSON.stringify({ ...value, savedAt: new Date().toISOString() }));
    // Apply the analytics decision immediately if PostHog is present.
    if (window.posthog) {
      if (value.analytics) window.posthog.opt_in_capturing();
      else window.posthog.opt_out_capturing();
    }
  } catch { /* ignore */ }
}

export default function CookieBanner() {
  const [state, setState] = useState(null);
  const [showCustom, setShowCustom] = useState(false);
  const [analytics, setAnalytics] = useState(false);

  useEffect(() => {
    const c = readConsent();
    setState(c);
    if (c) {
      if (window.posthog) {
        if (c.analytics) window.posthog.opt_in_capturing();
        else window.posthog.opt_out_capturing();
      }
    } else if (window.posthog) {
      // Default to opt-out until explicit consent
      window.posthog.opt_out_capturing();
    }
  }, []);

  if (state) return null;

  const acceptAll = () => {
    writeConsent({ essential: true, analytics: true, choice: "accept-all" });
    setState({ analytics: true });
  };
  const rejectAll = () => {
    writeConsent({ essential: true, analytics: false, choice: "reject-non-essential" });
    setState({ analytics: false });
  };
  const saveCustom = () => {
    writeConsent({ essential: true, analytics, choice: "custom" });
    setState({ analytics });
  };

  return (
    <div
      className="fixed inset-x-2 bottom-2 sm:inset-x-auto sm:right-4 sm:bottom-4 sm:max-w-md z-[60] rounded-2xl bg-slate-900 border border-white/10 shadow-2xl text-slate-100"
      role="dialog"
      aria-live="polite"
      data-testid="cookie-banner"
    >
      <div className="p-5 space-y-3">
        <div className="flex items-start gap-3">
          <Cookie className="w-5 h-5 text-amber-300 mt-0.5 shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="font-display font-bold text-white">Cookies en {"citastallerapp.com"}</div>
            <p className="text-sm text-slate-300 mt-1">
              Usamos cookies técnicas necesarias para el funcionamiento del servicio. Con tu permiso también usaremos cookies analíticas para mejorar la app. Consulta la{" "}
              <Link to="/cookies" className="text-blue-400 underline">Política de Cookies</Link>.
            </p>
          </div>
        </div>

        {showCustom && (
          <div className="border-t border-white/10 pt-3 space-y-3">
            <label className="flex items-start gap-2 text-sm opacity-70 cursor-not-allowed">
              <input type="checkbox" checked disabled className="mt-0.5" />
              <div>
                <div className="font-medium text-white">Cookies técnicas</div>
                <div className="text-xs text-slate-400">Necesarias. No pueden desactivarse.</div>
              </div>
            </label>
            <label className="flex items-start gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={analytics}
                onChange={(e) => setAnalytics(e.target.checked)}
                className="mt-0.5"
                data-testid="cookie-analytics-check"
              />
              <div>
                <div className="font-medium text-white">Cookies analíticas</div>
                <div className="text-xs text-slate-400">PostHog. Nos ayudan a entender qué funciones se usan.</div>
              </div>
            </label>
          </div>
        )}

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 pt-1">
          {showCustom ? (
            <>
              <button onClick={saveCustom} className="col-span-2 sm:col-span-3 px-3 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium" data-testid="cookie-save-custom">
                Guardar preferencias
              </button>
            </>
          ) : (
            <>
              <button onClick={rejectAll} className="px-3 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-white text-sm font-medium" data-testid="cookie-reject">
                Rechazar
              </button>
              <button onClick={() => setShowCustom(true)} className="px-3 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-white text-sm font-medium" data-testid="cookie-customize">
                Personalizar
              </button>
              <button onClick={acceptAll} className="col-span-2 sm:col-span-1 px-3 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium" data-testid="cookie-accept">
                Aceptar todo
              </button>
            </>
          )}
        </div>
      </div>
      <button
        onClick={rejectAll}
        className="absolute top-2 right-2 text-slate-400 hover:text-white p-1"
        aria-label="Cerrar y rechazar cookies opcionales"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
